<template>
    <ModalCommand
        :id="numeroVersion"
        :isOpen="isOpenCommandModal"
        :isElectronicSigningPossible="isFichierPdfPresent"
        :objet="objet"
        :montant="prix"
        :monnaie="monnaie"
        :initialNumCommande="numCommande ? `${numCommande}` : ''"
        :isLoading="isLoadingCommand"
        :errorMessage="commandErrorMessage"
        @commander-devis="commanderDevis"
        @commander-devis-signature-electronique="commanderDevisSignatureElectronique"
        @cancel="closeCommandModal"
    />
    <DocuSignModal
        :isOpen="isDocuSignModalOpen"
        :url="docuSignUrl"
        :devisNumero="numeroVersion"
        @close="onCancelSignature"
        @signature-event="onSignatureEvent"
    />
    <ModalRefuse
        @selectRefuse="selectRefuse"
        @refuseCancel="refuseCancel"
        @refuseConfirm="refuseConfirm"
        @setCommentValue="setCommentValue"
        :id="$route.params.value"
        :commentValue="commentValue"
        :isOpen="isOpenModalRefuse"
        :refuseSelected="refuseSelected"
    />
    <SuccessModal
        @cancel="isOpenSuccessModal = false"
        v-if="isOpenSuccessModal"
        :title="successMessage"
    />
    <WaitingModal v-if="isLoading"/>
    <div class="container">
        <div class="title-container">
            <span>
                {{ $t('devis.singlePage.title') }} {{ $route.params.value }}
                <ReglePastille cle="devis.definition" />
                <ReglePastille cle="devisDetail.actionsSelonStatut" />
            </span>
        </div>
        <BorderedIconButton title="RETOUR" :imgUrl="imgUrl" className="back-btn" :onClick="$router.back" />
        <StatusTag v-if="status !== null" :status="status" />
        <DataList v-if="devisData" :data="devisData" />


                <LoaderComponent v-if="isLoadingInterventions" />
        <div v-else-if="interventions.length > 0" class="dataTable" >

                <h2>
                    {{ countinterventions }}
                    {{ $tc('intervention.dataTable.title', countinterventions) }}
                </h2>
            <div ref="content" @scroll="handleScroll" class="datatable-container">
                <DataTable
                    :count="interventions.length"
                    :table-data="tableInterventionsData"
                    :table-header="tableHeaderInterventions"
                    @sort="doSort"

                />
                </div>
            </div>
        </div>

    <div class="bandeau-container">
        <div class="btn-container">
            <Button :disabled="!isFichierPdfPresent" :className="'whiteBtn'" :text="$t('seePDF')" :onClick="seePDF" />
            <ReglePastille cle="devisDetail.pdfDisponible" />
            <Button v-if="validStatus()" :className="'redBtn'" :text="$t('refuse')" :onClick="refuse" />
            <Button v-if="validStatus()" :className="'greenBtn'" :text="$t('command')" :onClick="command" />
            <ReglePastille v-if="validStatus()" cle="devisDetail.commanderDeuxModes" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import DataList from '@/components/ui-components/DataList.vue';
import DevisService, { CommanderDevis, DevisAsigner, RefusDevis } from '../services/DevisService';
import StatusTag, { statusName } from '@/components/ui-components/StatusTag.vue';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import currencyFormatter from 'currency-formatter';
import Button from '@/components/ui-components/Button.vue';
import SuccessModal from '@/components/ui-components/SuccessModal.vue';
import ModalRefuse from '../component/ModalRefuse.vue';
import ModalCommand from '../component/ModalCommand.vue';
import DocuSignModal from '../component/DocuSignModal.vue';
import { DEVIS } from '../store/mutation-types';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import { InterventionNonPreventive } from '@/models/InterventionNonPreventive';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import { TableHeader } from '@/models/TableHeader';
import { SortType, sortValue } from '@/models/SortType';
import { DevisActions } from '@/modules/Devis/store/action-types';
import ReglePastille from '@/components/regles/ReglePastille.vue';

enum RefuseType {
    SANS_SUITE = 'SANS_SUITE',
    NEGOTIATION = 'NEGOTIATION',
    PERDU = 'PERDU',
}

interface Data {
    numeroVersion: string;
    devisData: any[];
    status?: string;
    numero: number | null;
    numCommande: number | null;
    interventionsId: number[] | null;
    version: number | null;
    refuseSelected: RefuseType | null;
    isOpenModalRefuse: boolean;
    commentValue: string;
    chiffrageId: number;
    isOpenSuccessModal: boolean;
    isOpenCommandModal: boolean;
    isLoadingCommand: boolean;
    commandErrorMessage: string;
    objet: string;
    prix: number | null;
    monnaie: string;
    isDocuSignModalOpen: boolean;
    docuSignUrl: string;
    successMessage: string;
    isLoading: boolean;
    isFichierPdfPresent: boolean;
}

export default defineComponent({
    name: 'DevisDetailPage',
    components: { DataTable, LoaderComponent,  DataList, StatusTag, BorderedIconButton, Button, ModalRefuse, ModalCommand, DocuSignModal, SuccessModal, WaitingModal, ReglePastille },
    data(): Data {
        return {
            numeroVersion: '',
            devisData: [],
            numero: null,
            numCommande: null,
            interventionsId: null,
            version: null,
            status: undefined,
            refuseSelected: null,
            isOpenModalRefuse: false,
            commentValue: '',
            chiffrageId: 0,
            isOpenSuccessModal: false,
            isOpenCommandModal: false,
            isLoadingCommand: false,
            commandErrorMessage: '',
            objet: '',
            prix: null,
            monnaie: 'EUR',
            isDocuSignModalOpen: false,
            docuSignUrl: '',
            successMessage: this.$t('devis.modal.command.withoutSign.success.message'),
            isLoading: false,
            isFichierPdfPresent: false,
        };
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    mounted() {
        this.content?.scrollTo({ top: this.scrollInterventions });
    },
    beforeMount() {
        this.fetchDevis();
    },
    methods: {
        async fetchDevis() {
            this.$store.commit(DEVIS.RESET_ITEMS_INTERVENTIONS);

            DevisService.getDevis(+this.$route.params.id)
                .then((res: any) => {
                    this.numeroVersion = res.data.numero + '-' + res.data.version;
                    const {
                        etatDevis,
                        chiffrageId,
                        numero,
                        numCommande,
                        interventionsId,
                        version,
                        numFactures,
                        objet,
                        prix,
                        montantFacture,
                        monnaie,
                        dateChiffrage,
                        isFichierPdfPresent,
                        demandeDevis,
                    } = res.data;

                    this.chiffrageId = chiffrageId;
                    if (Array.isArray(interventionsId) && interventionsId.length > 0) {
                        this.getInterventionData();
                    }
                    this.numero = numero;
                    this.version = version;
                    this.numCommande = numCommande;
                    this.objet = objet || '';
                    this.prix = prix !== undefined ? prix : null;
                    this.monnaie = monnaie || 'EUR';

                    this.status = statusName(etatDevis);
                    this.isFichierPdfPresent = isFichierPdfPresent;

                    this.devisData = [
                        { label: this.$t('dataList.devisId'), value: numero+'-'+version },
                        { label: this.$t('dataList.commandeId'), value: numCommande },
                        { label: this.$t('dataList.interventionId'), value: interventionsId?.[0] },
                        { label: this.$t('dataList.numFactures'), value: this.displayNumFactures(numFactures) },
                        { label: this.$t('dataList.devisObject'), value: objet },
                        {
                            label: this.$t('dataList.dateChiffrage'),
                            value: new Date(dateChiffrage).toLocaleDateString(),
                        },
                        {
                            label: this.$t('dataList.devisPrice'),
                            value: currencyFormatter.format(prix, { code: monnaie }),
                        },
                        {
                            label: this.$t('dataList.facturePrice'),
                            value: montantFacture ? currencyFormatter.format(montantFacture, { code: monnaie }) : '',
                        },
                        { label: this.$t('dataList.devis.demandedevis.id'), value: {id: demandeDevis?.id, pageGoTo: 'devisRequestDetailView'} },
                        { label: this.$t('dataList.devis.demandedevis.objet'), value: demandeDevis?.objet },
                    ];
                })
                .catch(async (err: any) => {
                    const message = await ErrorManager.processError(err.response);

                    this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
                    this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message);
                });
        },
        seePDF() {
            if (this.numero && this.version) {
                this.isLoading = true;
                DevisService.getDevisPDF(this.numero, this.version).then(async response => {
                    if(response.data != null){
                        this.isLoading = false;
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `devis${this.numero}-${this.version}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                    }
                });
            }
        },
        refuse() {
            this.isOpenModalRefuse = true;
        },
        command() {
            this.isOpenCommandModal = true;
            this.commandErrorMessage = '';
        },
        closeCommandModal() {
            this.isOpenCommandModal = false;
            this.commandErrorMessage = '';
            this.isLoadingCommand = false;
        },
        async onCancelSignature() {
            try {
                if (this.chiffrageId) {
                    await DevisService.deleteSignature(+this.chiffrageId);
                }
                this.fetchDevis();
            } catch (error) {
                console.error('Erreur annulation signature', error);
                this.$store.commit(
                    `errorToaster/${ERROR.SET_MESSAGE}`,
                    await ErrorManager.processError(error),
                    {
                        root: true,
                    }
                );
            } finally {
                this.isDocuSignModalOpen = false;
                this.docuSignUrl = '';
            }
        },
        async onSignatureEvent(event: string) {
            if (event === 'signing_complete') {
                this.isDocuSignModalOpen = false;
                this.docuSignUrl = '';
                this.successMessage = this.$t('devis.modal.docusign.success') || 'Le devis a été signé avec succès !';
                this.isOpenSuccessModal = true;
                this.$store.commit(DEVIS.RESET_ITEMS);
                this.$store.commit(DEVIS.REMOVE_BONCOMMANDE);
                this.fetchDevis();
            } else if (
                event === 'cancel' ||
                event === 'decline' ||
                event === 'session_timeout' ||
                event === 'ttl_expired' ||
                event === 'exception'
            ) {
                await this.onCancelSignature();
            } else {
                this.isDocuSignModalOpen = false;
                this.docuSignUrl = '';
                this.fetchDevis();
            }
        },
        async commanderDevisSignatureElectronique(devisASigner: DevisAsigner) {
            if (!this.chiffrageId) {
                this.commandErrorMessage = this.$t('devis.modal.docusign.initError');
                return;
            }

            this.isLoadingCommand = true;
            this.commandErrorMessage = '';
            try {
                const userInfo = this.$store.getters.getUserInformations;
                const returnUrl = `${window.location.origin}/signature-callback.html`;

                const response = await DevisService.postSignerDevis({
                    chiffrageId: +this.chiffrageId,
                    nom: devisASigner.nom || userInfo?.nom,
                    prenom: devisASigner.prenom || userInfo?.prenom,
                    email: devisASigner.email || userInfo?.email || userInfo?.email_azure,
                    fonction: userInfo?.fonctions?.[0],
                    returnUrl,
                });

                this.isLoadingCommand = false;
                if (response?.data?.url) {
                    this.isOpenCommandModal = false;
                    this.docuSignUrl = response.data.url;
                    this.isDocuSignModalOpen = true;
                } else {
                    this.commandErrorMessage = this.$t('devis.modal.docusign.initError');
                }
            } catch (error: any) {
                this.isLoadingCommand = false;
                if (error?.response?.status === 403) {
                    this.commandErrorMessage = this.$t('devis.modal.docusign.alreadySigning');
                    this.$store.commit(
                        `errorToaster/${ERROR.SET_MESSAGE}`,
                        this.$t('devis.modal.docusign.alreadySigning'),
                        { root: true }
                    );
                } else {
                    const message = await ErrorManager.processError(error);
                    this.commandErrorMessage = message || this.$t('devis.modal.docusign.initError');
                    this.$store.commit(
                        `errorToaster/${ERROR.SET_MESSAGE}`,
                        this.commandErrorMessage,
                        { root: true }
                    );
                }
            }
        },
        commanderDevis(numeroCommande: string) {
            this.isLoadingCommand = true;
            this.commandErrorMessage = '';
            const bonCommandeFile = this.$store.getters.getBonCommande?.[0];
            DevisService.postCommanderEstimate(new CommanderDevis(this.chiffrageId, numeroCommande), bonCommandeFile)
                .then(res => {
                    this.isLoadingCommand = false;
                    if (res.status === 200) {
                        this.isOpenCommandModal = false;
                        this.successMessage = this.$t('devis.modal.command.withoutSign.success.message');
                        this.isOpenSuccessModal = true;
                        this.$store.commit(DEVIS.RESET_ITEMS);
                        this.$store.commit(DEVIS.REMOVE_BONCOMMANDE);
                        this.fetchDevis();
                    }
                })
                .catch(async error => {
                    this.isLoadingCommand = false;
                    const message = await ErrorManager.processError(error);
                    this.commandErrorMessage = message;
                    this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message, {
                        root: true,
                    });
                });
        },
        refuseCancel() {
            this.isOpenModalRefuse = false;
            this.resetRefuseForm();
        },
        resetRefuseForm() {
            this.refuseSelected = null;
            this.commentValue = '';
        },
        refuseConfirm() {
            if (this.refuseSelected && this.chiffrageId) {
                this.isLoading = true;
                DevisService.postRefuseEstimate(new RefusDevis(+this.chiffrageId, this.refuseSelected, this.commentValue))
                    .then(() => {
                        this.isLoading = false;
                        this.successMessage = this.$t('devis.modal.refuse.success.message');
                        this.isOpenSuccessModal = true;
                        this.$store.commit(DEVIS.RESET_ITEMS);
                        this.fetchDevis();
                    })
                    .catch(async error => {
                        this.isLoading = false;
                        this.$store.commit(
                            `errorToaster/${ERROR.SET_MESSAGE}`,
                            await ErrorManager.processError(error),
                            {
                                root: true,
                            }
                        );
                    });
            }
            this.isOpenModalRefuse = false;

            this.resetRefuseForm();
        },
        selectRefuse(answer: RefuseType) {
            if (this.refuseSelected !== answer) this.refuseSelected = answer;
            else this.refuseSelected = null;
        },
        setCommentValue(value: string) {
            this.commentValue = value;
        },
        validStatus() {
            return (
                this.status != this.$t('state.REFUSE') &&
                this.status != this.$t('state.ENCOMMANDE') &&
                this.status != this.$t('state.REALISE') &&
                this.status != this.$t('state.REALISEE') &&
                this.status != this.$t('state.EN_SIGNATURE') &&
                this.status != this.$t('state.COMMANDE_TRANSMISE')
            );
        },
        displayNumFactures(listNumFactures: string[]) {
            return listNumFactures?.join(', ') || '';
        },
        getInterventionData(page = 0) {

            if (page === 0) {
                this.content?.scrollTo({ top: 0 });
            }

            const payload: any = {
                page,
                devisId: this.chiffrageId
            };

            if (this.sortInterventions && this.sortInterventions.sortData) {
                payload.sort = sortValue(
                    this.sortInterventions.sortData,
                    this.sortInterventions.sortStatus
                );
            }

            this.$store.dispatch(DevisActions.GET_INTERVENTIONS, payload);
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            this.$store.commit(DEVIS.SET_SCROLL_INTERVENTIONS, top);
            if (max - top <= height * 1.5 && !this.isLoadingInterventions  && this.page + 1 < this.numberOfPages) {
                this.getInterventionData(this.page + 1);
            }
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(DEVIS.SET_SORT_INTERVENTIONS, { sortData, sortStatus }) ;
            this.$store.commit(DEVIS.RESET_ITEMS_INTERVENTIONS);

            this.getInterventionData();
        },
    },
    computed: {
        isLoadingInterventions() {
            return this.$store.getters.getInterventionsIsLoading;
        },
        interventions(): InterventionNonPreventive[] {
            return this.$store.getters.getInterventionsItems;
        },
        countinterventions(): number {
            return this.interventions.length;
        },
        imgUrl() {
            return require('@/assets/img/chevron-left-white.svg');
        },
        tableInterventionsData() {
            return this.interventions.map(({ id, objet, dateDebutPrevue, dateFinReelle, etat }) => ({
                id: { pageName: 'interventionDetailView', value: id, label: id },
                objet,
                dateDebutPrevue: dateDebutPrevue ? new Date(dateDebutPrevue).toLocaleDateString() : '',
                dateFinReelle: dateFinReelle ? new Date(dateFinReelle).toLocaleDateString() : '',
                statut: statusName(etat),
            }));
        },

        tableHeaderInterventions(): TableHeader[] {
            return this.$store.getters.getInterventionsTableHeader || [];
        },
        scrollInterventions() {
            return this.$store.getters.getInterventionsScroll;
        },
        sortInterventions(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters.getInterventionsSort;
        },

        page() {
            return this.$store.getters.getInterventionsPage;
        },
        numberOfPages() {
            return this.$store.getters.getInterventionsNbOfPages;
        },
    },
});
</script>

<style scoped>

.container {
    display: flex;
    flex-direction: column;
    max-height: 95vh;
    padding: 24px;
    padding-bottom: 60px;
    box-sizing: border-box;
    overflow: hidden;
}


.title-container {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-600);
    margin-bottom: 15px;
}

.dataTable {
    padding: 8px 16px 4px 16px;
    background: white;
    border-radius: 6px;
}

.dataTable > div {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
}

h2 {
    font-weight: 500;
    font-size: 18px;
    color: var(--color-principal);
    margin: 0 0 0 0;
}
.datatable-container {
    overflow-y: auto;
    max-height: calc(100vh - 610px);
    margin-top: 5px;
}

@media screen and (max-width: 1631px) {
    .datatable-container {
        max-height: calc(100vh - 560px);
    }
}

@media screen and (max-width: 1531px) {
    .datatable-container {
        max-height: calc(100vh - 610px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 660px);
    }
}
.navigation-container {
    position: fixed;
    width: calc(100% - 2px);
    height: 8vh;
    bottom: 6.7vh;
}
.content-container {
    margin-top: 8px;
}
.title-container {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-600);
    margin-bottom: 15px;
}
.bandeau-container {
    box-sizing: border-box;
    background-color: var(--medium-grey);
    border: 1px solid var(--medium-light-grey);
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 56px;
}
.btn-container {
    margin-left: auto;
    margin-right: 90px;
}
button {
    font-family: 'Raleway Bold', sans-serif;
    font-variant-numeric: lining-nums;
    margin: 0 8px;
}
</style>
